import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CallActivityGraph from "../reusables/CallActivotyGraph";

export const CHART_SERIES = [
  { id: "total", label: "Total calls", color: "#8B5CF6" },
  { id: "inbound", label: "Inbound", color: "#8B5CF6" },
  { id: "outbound", label: "Outbound", color: "#F59E0B" },
  { id: "voicemails", label: "Voicemails", color: "#6366F1" },
];

export const filterCallsForSeries = (calls, seriesId) => {
  const list = calls || [];
  switch (seriesId) {
    case "inbound":
      return list.filter((c) => c.action !== "Outbound Call");
    case "outbound":
      return list.filter((c) => c.action === "Outbound Call");
    case "voicemails":
      return list.filter((c) => c.voicemail === "Yes");
    case "total":
    default:
      return list;
  }
};

const CallVolumeModal = ({
  open,
  onClose,
  calls,
  dateRange,
  startDate,
  endDate,
  isLoading,
  initialSeries = "total",
}) => {
  const [frequency, setFrequency] = useState("daily");
  const [series, setSeries] = useState(initialSeries);

  // Sync series when opening from a specific card
  useEffect(() => {
    if (open) {
      setSeries(initialSeries || "total");
    }
  }, [open, initialSeries]);

  const seriesCalls = useMemo(
    () => filterCallsForSeries(calls, series),
    [calls, series]
  );

  const activeSeries = CHART_SERIES.find((s) => s.id === series) || CHART_SERIES[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: "rounded-xl",
        sx: { borderRadius: "12px" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          pr: 1,
          pb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <span className="text-lg font-semibold text-gray-900">Call Volume</span>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5"
            role="tablist"
            aria-label="Chart series"
          >
            {CHART_SERIES.map((s) => {
              const active = series === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setSeries(s.id)}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    active
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          <Select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            size="small"
            sx={{
              fontSize: "13px",
              bgcolor: "white",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E5E7EB",
              },
            }}
          >
            <MenuItem value="hourly">Hourly</MenuItem>
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
          </Select>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Box className="h-[360px]">
          <CallActivityGraph
            call_data={seriesCalls}
            dateRange={dateRange}
            frequency={frequency}
            isLoading={isLoading}
            startDate={startDate}
            endDate={endDate}
            hideHeader
            strokeColor={activeSeries.color}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CallVolumeModal;
